import type { Dispatch, SetStateAction } from 'react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { MemberPortalProfile, ProfileDraft } from './MemberPortalProfile'
import { COUNTRY_CODES, countryName } from '@/lib/countries'

type Props = {
  current: MemberPortalProfile
  draft: ProfileDraft
  editing: boolean
  onDraft: Dispatch<SetStateAction<ProfileDraft | null>>
  errors: Record<string, string[]>
}
export function MemberPortalPostalFields({ current, draft, editing, onDraft, errors }: Props) {
  const fields = [
    ['Address line 1', 'addressLine1', 'address_line1'],
    ['Address line 2', 'addressLine2', 'address_line2'],
    ['City', 'city', 'city'],
    ['Region', 'region', 'region'],
    ['Postcode', 'postalCode', 'postal_code'],
  ] as const
  if (!editing) {
    return (
      <div className="space-y-2 border-t border-border pt-3">
        {fields.map(([label, , errorKey]) => (
          <div key={errorKey} className="flex flex-col gap-1 py-1 sm:flex-row sm:gap-4">
            <span className="w-full shrink-0 text-body-sm text-muted-foreground sm:w-40">
              {label}
            </span>
            <span className="text-body-sm text-foreground">{current[errorKey] ?? '-'}</span>
          </div>
        ))}
        <div className="flex flex-col gap-1 py-1 sm:flex-row sm:gap-4">
          <span className="w-full shrink-0 text-body-sm text-muted-foreground sm:w-40">
            Country
          </span>
          <span className="text-body-sm text-foreground">
            {current.country_code ? countryName(current.country_code) : '-'}
          </span>
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-2 border-t border-border pt-3">
      {fields.map(([label, draftKey, errorKey]) => (
        <label
          key={draftKey}
          className="block text-body-sm text-muted-foreground"
          htmlFor={`profile-${draftKey}`}
        >
          {label}
          <Input
            id={`profile-${draftKey}`}
            value={editing ? draft[draftKey] : (current[errorKey] ?? '')}
            onChange={(event) => onDraft({ ...draft, [draftKey]: event.target.value })}
          />
          {errors[errorKey]?.map((error) => (
            <span key={error} className="text-destructive">
              {error}
            </span>
          ))}
        </label>
      ))}
      <label className="block text-body-sm text-muted-foreground" htmlFor="profile-country">
        Country
        <Select
          value={draft.countryCode}
          onValueChange={(value) => onDraft({ ...draft, countryCode: value })}
        >
          <SelectTrigger id="profile-country">
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((code) => (
              <SelectItem key={code} value={code}>
                {countryName(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
    </div>
  )
}
